import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface Step {
  id: string;
  name: string;
  description: string;
  status: 'not-started' | 'in-progress' | 'completed' | 'delayed' | 'needs-update';
  dueDate?: string;
}

interface PhaseSectionProps {
  id: string;
  name: string;
  description: string;
  status: 'not-started' | 'in-progress' | 'completed' | 'delayed' | 'needs-update';
  steps: Step[];
  isExpanded: boolean;
  onToggle: () => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'in-progress':
      return 'bg-blue-100 text-blue-800';
    case 'delayed':
      return 'bg-red-100 text-red-800';
    case 'needs-update':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function PhaseSection({
  name,
  description,
  status,
  steps,
  isExpanded,
  onToggle,
}: PhaseSectionProps) {
  return (
    <div className="border-b pb-6 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between mb-4 focus:outline-none"
      >
        <div className="flex items-center space-x-2">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-500" />
          )}
          <h3 className="text-lg font-medium">{name}</h3>
        </div>
        <span className={clsx(
          'inline-flex items-center rounded-full px-3 py-0.5 text-sm font-medium',
          getStatusColor(status)
        )}>
          {status.replace('-', ' ').toUpperCase()}
        </span>
      </button>
      <div
        className={clsx(
          'transition-all duration-200 ease-in-out overflow-hidden',
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <p className="text-gray-600 mb-4">{description}</p>
        <div className="space-y-4">
          {steps.map((step) => (
            <div key={step.id} className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{step.name}</h4>
                {step.dueDate && (
                  <span className="text-sm text-gray-500">
                    Due: {step.dueDate}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-600">{step.description}</p>
              <div className="mt-3">
                <span className={clsx(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  getStatusColor(step.status)
                )}>
                  {step.status.replace('-', ' ').toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}